import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import doxezLogo from '../../assets/DoxezLOGO.png';
import {
    CheckCircle2,
    Clock,
    XCircle,
    ArrowRight,
    Receipt,
    CreditCard,
    Building2,
    Activity,
    RefreshCw
} from 'lucide-react';

const PaymentStatus = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [lead, setLead] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStatus = async (showLoading = false) => {
        try {
            if (showLoading) setLoading(true);

            const rawApi = import.meta.env.VITE_API_URL || '';
            const apiBase = rawApi.endsWith('/api') ? rawApi : (rawApi ? `${rawApi}/api` : '/api');

            // Auto-sync with Razorpay
            try {
                const queryPaymentId = searchParams.get('razorpay_payment_id') || searchParams.get('payment_id');
                const queryLinkId = searchParams.get('razorpay_payment_link_id');
                await axios.post(`${apiBase}/payments/public-sync`, {
                    leadId: id,
                    razorpayPaymentId: queryPaymentId,
                    razorpayPaymentLinkId: queryLinkId
                });
            } catch (syncErr) {
                console.warn('[PaymentStatus] Sync failed:', syncErr.message);
            }

            const res = await axios.get(`${apiBase}/leads/public/${id}`);
            if (res.data?.data) {
                setLead(res.data.data);
            } else {
                throw new Error('Lead data missing');
            }
        } catch (err) {
            console.error('[PaymentStatus] Error:', err);
            if (err.response?.status === 404 || err.response?.status === 400) {
                setLead(null);
            }
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchStatus(false);
        setRefreshing(false);
    };

    useEffect(() => {
        if (id) fetchStatus(true);
    }, [id]);

    const isConfirmed = lead && (
        ['paid', 'surgery', 'closure', 'payout_processing', 'completed', 'care_in_progress'].includes(lead.status) ||
        lead.ophePaymentData?.status === 'paid' ||
        lead.paymentStatus === 'paid' ||
        (lead.registrationStep && lead.registrationStep >= 2)
    );

    useEffect(() => {
        let interval;
        if (lead && !isConfirmed) {
            interval = setInterval(() => fetchStatus(false), 5000);
        }
        return () => clearInterval(interval);
    }, [id, lead?.status, isConfirmed]);

    // ─── Loading State ────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.loadingWrap}>
                    <div style={styles.spinner} />
                    <p style={styles.loadingText}>Verifying Payment…</p>
                </div>
            </div>
        );
    }

    // ─── Error State ──────────────────────────────────────────────────────────
    if (!lead) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    {/* Header */}
                    <div style={styles.cardHeader}>
                        <img src={doxezLogo} alt="Doxez" style={styles.logo} />
                    </div>
                    <div style={{ padding: '40px 32px', textAlign: 'center' }}>
                        <div style={{ ...styles.iconCircle, background: '#FEE2E2' }}>
                            <XCircle size={32} color="#EF4444" />
                        </div>
                        <h2 style={{ ...styles.title, marginTop: 20 }}>Link Error</h2>
                        <p style={styles.subtitle}>This payment link is invalid, expired, or the record has been archived.</p>
                        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <button style={styles.btnPrimary} onClick={() => fetchStatus(true)}>Retry</button>
                            <button style={styles.btnSecondary} onClick={() => navigate('/')}>Back to Home</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const { status, patientName, assignedHospital } = lead;
    const isOpheLead = lead?.pipelineChoice === 'ophe' || lead?.leadType === 'ophe' || Boolean(lead?.ophePaymentData);
    const queryPaymentId = searchParams.get('razorpay_payment_id') || searchParams.get('payment_id');
    const paymentIdToShow = queryPaymentId || lead?.ophePaymentData?.razorpayPaymentId || lead?.razorpayDetails?.paymentId;
    const amountRaw = lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.quotedPrice || lead?.billingDetails?.totalPayableAmount || lead?.billingDetails?.procedureAmount || searchParams.get('amount');
    const amountToShow = amountRaw && Number(amountRaw) > 0 ? Number(amountRaw) : null;

    // ─── Main Card ────────────────────────────────────────────────────────────
    return (
        <div style={styles.page}>
            {/* Animated background blobs */}
            <div style={styles.blob1} />
            <div style={styles.blob2} />

            <div style={styles.card}>
                {/* Dark Header with Logo */}
                <div style={styles.cardHeader}>
                    <img src={doxezLogo} alt="Doxez" style={styles.logo} />
                </div>

                {/* Accent bar */}
                <div style={{ height: 4, background: isConfirmed ? 'linear-gradient(90deg,#10b981,#34d399)' : 'linear-gradient(90deg,#3b82f6,#60a5fa)' }} />

                <div style={styles.cardBody}>
                    {/* Status Icon */}
                    <div style={{ position: 'relative', marginBottom: 24 }}>
                        <div style={{
                            ...styles.iconCircle,
                            background: isConfirmed ? 'rgba(16,185,129,0.12)' : 'rgba(59,130,246,0.12)',
                            width: 96,
                            height: 96,
                        }}>
                            {/* Ping ring */}
                            <div style={{
                                position: 'absolute', inset: 0, borderRadius: '50%',
                                background: isConfirmed ? 'rgba(16,185,129,0.2)' : 'rgba(59,130,246,0.2)',
                                animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite',
                            }} />
                            <div style={{
                                width: 60, height: 60, borderRadius: '50%',
                                background: isConfirmed ? '#10b981' : '#3b82f6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: isConfirmed ? '0 8px 24px rgba(16,185,129,0.35)' : '0 8px 24px rgba(59,130,246,0.35)',
                            }}>
                                {isConfirmed
                                    ? <CheckCircle2 size={28} color="#fff" />
                                    : <Clock size={28} color="#fff" />
                                }
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <h1 style={styles.title}>
                        {isConfirmed ? 'Payment Successful!' : 'Payment Processing'}
                    </h1>
                    <p style={styles.subtitle}>
                        {isConfirmed
                            ? 'Your transaction is confirmed. The Doxez Operations team has been notified.'
                            : 'We are verifying your payment with the bank. This usually takes a few seconds.'}
                    </p>

                    {/* Info Card */}
                    <div style={styles.infoCard}>
                        {/* Patient */}
                        <InfoRow
                            icon={<Activity size={20} color="#10b981" />}
                            label="Patient Name"
                            value={patientName || '—'}
                        />
                        <Divider />

                        {/* Hospital / Care Service */}
                        <InfoRow
                            icon={<Building2 size={20} color="#3b82f6" />}
                            label={isOpheLead ? 'Care Service' : 'Hospital'}
                            value={isOpheLead ? (lead?.opheServiceDetails?.packageName || 'OPHE Home Care Service') : (assignedHospital?.hospitalName || 'Clinical Partner')}
                        />

                        {/* Amount */}
                        {amountToShow && (
                            <>
                                <Divider />
                                <InfoRow
                                    icon={<CreditCard size={20} color="#8b5cf6" />}
                                    label="Amount Paid"
                                    value={`₹${amountToShow.toLocaleString('en-IN')}`}
                                    valueStyle={{ color: '#10b981', fontSize: 22, fontWeight: 800 }}
                                />
                            </>
                        )}

                        {/* Payment ID */}
                        {paymentIdToShow && (
                            <>
                                <Divider />
                                <InfoRow
                                    icon={<Receipt size={20} color="#f59e0b" />}
                                    label="Payment ID"
                                    value={paymentIdToShow}
                                    valueStyle={{ fontFamily: 'monospace', fontSize: 13, color: '#475569', userSelect: 'all' }}
                                />
                            </>
                        )}
                    </div>

                    {/* Buttons */}
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28 }}>
                        <button
                            style={{ ...styles.btnPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: refreshing ? 0.7 : 1 }}
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            <RefreshCw size={16} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                            {refreshing ? 'Refreshing…' : 'Refresh Status'}
                        </button>
                        <button
                            style={{ ...styles.btnSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            onClick={() => navigate('/login')}
                        >
                            Back to Portal <ArrowRight size={16} />
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <p style={styles.footerText}>🔒 Secured by Doxez Fintech · Powered by Razorpay</p>
                </div>
            </div>

            {/* CSS Animations */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                @keyframes ping {
                    75%, 100% { transform: scale(1.6); opacity: 0; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(24px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes spinLoader {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const InfoRow = ({ icon, label, value, valueStyle = {} }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
            width: 44, height: 44, borderRadius: 14,
            background: '#fff', border: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
            {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0, marginBottom: 2 }}>
                {label}
            </p>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0, wordBreak: 'break-all', ...valueStyle }}>
                {value}
            </p>
        </div>
    </div>
);

const Divider = () => (
    <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        position: 'relative',
        overflow: 'hidden',
    },
    blob1: {
        position: 'fixed', top: -100, right: -100,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    blob2: {
        position: 'fixed', bottom: -100, left: -100,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    card: {
        width: '100%',
        maxWidth: 480,
        background: '#ffffff',
        borderRadius: 32,
        overflow: 'hidden',
        boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)',
        animation: 'fadeIn 0.4s ease-out',
        position: 'relative',
        zIndex: 1,
    },
    cardHeader: {
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    logo: {
        height: 44,
        objectFit: 'contain',
        filter: 'brightness(1)',  // logo is white, will show on dark bg
    },
    cardBody: {
        padding: '36px 32px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    iconCircle: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    title: {
        fontSize: 26,
        fontWeight: 900,
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.03em',
    },
    subtitle: {
        fontSize: 14,
        fontWeight: 500,
        color: '#64748b',
        margin: '10px 0 0',
        lineHeight: 1.6,
        maxWidth: 320,
    },
    infoCard: {
        width: '100%',
        marginTop: 28,
        background: '#f8fafc',
        borderRadius: 20,
        padding: '8px 20px',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        textAlign: 'left',
    },
    btnPrimary: {
        width: '100%',
        padding: '16px 24px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: 16,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: "'Inter', sans-serif",
        letterSpacing: '0.04em',
        cursor: 'pointer',
        textTransform: 'uppercase',
        boxShadow: '0 8px 20px rgba(15,23,42,0.3)',
        transition: 'transform 0.15s, box-shadow 0.15s',
    },
    btnSecondary: {
        width: '100%',
        padding: '16px 24px',
        background: '#fff',
        color: '#475569',
        border: '1.5px solid #e2e8f0',
        borderRadius: 16,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: "'Inter', sans-serif",
        letterSpacing: '0.04em',
        cursor: 'pointer',
        textTransform: 'uppercase',
        transition: 'background 0.15s',
    },
    footer: {
        background: '#f8fafc',
        borderTop: '1px solid #f1f5f9',
        padding: '16px 32px',
        textAlign: 'center',
    },
    footerText: {
        fontSize: 11,
        fontWeight: 600,
        color: '#94a3b8',
        margin: 0,
        letterSpacing: '0.05em',
    },
    loadingWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
    },
    spinner: {
        width: 48,
        height: 48,
        border: '4px solid rgba(255,255,255,0.15)',
        borderTop: '4px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spinLoader 0.8s linear infinite',
    },
    loadingText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        margin: 0,
    },
};

export default PaymentStatus;
